import { createStore } from 'zustand';
import {
  BusinessDistance,
  FinanceOption,
  ModelType,
  PrivateDistance,
} from '../../../../types';
import {
  BusinessEditionFilter,
  CarouselAction,
  CarouselOptions,
  CarouselStore,
  ImageTypeEnum,
} from '../types/CarouselTypes';

const defaultOptions: CarouselOptions = {
  imageType: ImageTypeEnum.STUDIO_BG,
  fields: [],
};

const getFilteredModels = (state: CarouselStore): ModelType[] => {
  let filtered: ModelType[];
  if (
    !state.selectedFinanceOption &&
    !state.selectedFuels.length &&
    !state.selectedCategories.length
  ) {
    filtered = [...state.models];
  } else {
    filtered = [...state.models].filter((model) => {
      let shouldReturn = true;
      if (state.selectedFuels.length) {
        if (!state.selectedFuels.includes(model.type.id)) {
          shouldReturn = false;
        }
      }
      if (state.selectedCategories.length) {
        if (
          !state.selectedCategories.some((category) =>
            model.categories.map((c) => c.id).includes(category),
          )
        ) {
          shouldReturn = false;
        }
      }
      if (state.selectedFinanceOption) {
        switch (state.selectedFinanceOption) {
          case FinanceOption.PRIVATE:
            if (!model.visible_cars[0]?.private_leasing?.length) {
              shouldReturn = false;
            }
            break;
          case FinanceOption.BUSINESS:
            if (!model.visible_cars[0].business_leasing?.length) {
              shouldReturn = false;
            }
            break;
          case FinanceOption.LOAN:
            if (!model.visible_cars[0].loan?.length) {
              shouldReturn = false;
            }
            break;
          default:
            break;
        }
      }
      return shouldReturn;
    });
  }

  if (state.businessEditionFilter === 'only') {
    filtered = filtered.filter((m) =>
      m.visible_cars?.some((c) => c.business_edition),
    );
  } else if (state.businessEditionFilter === 'exclude') {
    filtered = filtered.filter((m) =>
      m.visible_cars?.some((c) => !c.business_edition),
    );
  }

  return filtered;
};

const reducer = (
  state: CarouselStore,
  action: CarouselAction,
): CarouselStore => {
  if (action.type === 'SHOW_PRIVATE_MILEAGES') {
    return {
      ...state,
      showPrivateMileages: action.payload,
    };
  }
  if (action.type === 'SET_BUSINESS_EDITION_FILTER') {
    return {
      ...state,
      businessEditionFilter: action.payload,
    };
  }
  if (action.type === 'SHOW_PRICELIST') {
    return action.payload
      ? { ...state, options: undefined }
      : { ...state, options: defaultOptions };
  } else if (action.type === 'HEADING') {
    return { ...state, currentHeading: action.payload };
  } else if (action.type === 'IMAGE_TYPE') {
    return {
      ...state,
      options: { ...state.options, imageType: action.payload },
    };
  } else if (action.type === 'CONTROLS') {
    return { ...state, options: { ...state.options, fields: action.payload } };
  } else if (action.type === 'DIRECTION') {
    return {
      ...state,
      direction: action.payload,
    };
  } else if (action.type === 'MODELS') {
    return { ...state, models: action.payload };
  } else if (action.type === 'FILTERED_MODELS') {
    return { ...state, filteredModels: action.payload };
  } else if (action.type === 'FUEL') {
    return { ...state, selectedFuels: action.payload };
  } else if (action.type === 'FINANCE_OPTION') {
    return { ...state, selectedFinanceOption: action.payload };
  } else if (action.type === 'BUSINESS_DISTANCE') {
    return {
      ...state,
      selectedBusinessDistance: action.payload,
    };
  } else if (action.type === 'PRIVATE_DISTANCE') {
    return {
      ...state,
      selectedPrivateDistance: action.payload,
    };
  } else if (action.type === 'IS_LOADED') {
    return { ...state, isLoaded: action.payload };
  } else if (action.type === 'FILTER_MODELS') {
    return { ...state, filteredModels: getFilteredModels(state) };
  } else if (action.type === 'CATEGORY') {
    return { ...state, selectedCategories: action.payload };
  } else if (action.type === 'SORT_BY') {
    return {
      ...state,
      sortField: action.payload,
      sortDirection: action.sortBy,
    };
  }
  return state;
};

const createCarouselStore = () =>
  createStore<CarouselStore>()((set) => ({
    models: [],
    filteredModels: [],
    selectedFuels: [],
    selectedCategories: [],
    currentHeading: '',
    selectedFinanceOption: undefined,
    direction: 'horizontal',
    options: undefined,
    isLoaded: false,
    showPrivateMileages: false,
    businessEditionFilter: 'exclude' as BusinessEditionFilter,
    selectedBusinessDistance: BusinessDistance.One,
    selectedPrivateDistance: PrivateDistance.One,
    sortField: null,
    sortDirection: 'asc',
    dispatch: (args: CarouselAction) =>
      set((state: CarouselStore) => reducer(state, args)),
  }));

const carouselStoresMap = new Map<
  string,
  ReturnType<typeof createCarouselStore>
>();

const createCarouselStoreFactory = (
  carouselStores: typeof carouselStoresMap,
) => {
  return (carouselStoreKey: string) => {
    if (!carouselStores.has(carouselStoreKey)) {
      carouselStores.set(carouselStoreKey, createCarouselStore());
    }
    return carouselStores.get(carouselStoreKey)!;
  };
};

export const getStoreByKey = createCarouselStoreFactory(carouselStoresMap);
